// src/routes/RouteApp.js
import React, { Fragment, Suspense } from 'react';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '../context/AuthContext';
import SessionTimeout from './SessionTimeout'; // Importa o componente de expiração de sessão
import AppLayout from './AppLayout'; // Layout persistente (Navbar fixa + conteúdo da rota)

// ⚡ Importações NORMAIS - Componentes pequenos/críticos que devem carregar rápido
import Login from '../pages/login/login';
import Unauthorized from '../pages/unauthorized/unauthorized';

// 🚀 LAZY LOADING - Componentes pesados que serão carregados sob demanda
const Home = React.lazy(() => import('../pages/home/home'));
const CompanyInfo = React.lazy(() => import('../pages/company_info/company_info'));
const Estimativa = React.lazy(() => import('../pages/estimativa/estimativa'));
const ReportList = React.lazy(() => import('../pages/relatorios/reportlist'));
const Inventario = React.lazy(() => import('../pages/relatorios/inventario'));
const Movimentacao = React.lazy(() => import('../pages/relatorios/movimentacao'));
const ProdutividadeVistorias = React.lazy(() => import('../pages/relatorios/produtividade_vistorias'));
const Carregamento = React.lazy(() => import('../pages/carregamento/carregamento'));
const JanelasDevolucao = React.lazy(() => import('../pages/janelas/janelas_devolucao'));
const GateIn = React.lazy(() => import('../pages/gate/gate_in'));
const GateOut = React.lazy(() => import('../pages/gate/gate_out'));
const OpenTurnIn = React.lazy(() => import('../pages/gate/open_turn_in'));
const PreEntradaLote = React.lazy(() => import('../pages/gate/pre_entrada_lote'));
const GerenciamentoLacres = React.lazy(() => import('../pages/gate/gerenciamento_lacres'));
const NotasAtualizacaoPage = React.lazy(() => import('../pages/notas_atualizacao/notas_atualizacao_page'));
const NoShow = React.lazy(() => import('../pages/no_show/no_show'));
const Parceiros = React.lazy(() => import('../pages/parceiros/parceiros'));
const PagamentoLote = React.lazy(() => import('../pages/avaria/pagamento_lote'));
const SobreRodas = React.lazy(() => import('../pages/avaria/sobre_rodas'));
const ControleCobrancas = React.lazy(() => import('../pages/avaria/controle_cobrancas'));
const CobrancaLote = React.lazy(() => import('../pages/avaria/cobranca_lote'));
const PainelVistorias = React.lazy(() => import('../pages/estimativa/painel_vistorias'));
const ProtocoloEstimativa = React.lazy(() => import('../pages/estimativa/protocolo_est'));
const AtualizacaoStatus = React.lazy(() => import('../pages/documentos/atualizacao_status'));
const IsencoesADefinir = React.lazy(() => import('../pages/documentos/isencoes_a_definir'));
const CobrancaCliente = React.lazy(() => import('../pages/cobranca/cobranca_cliente'));
const CobrancaLoteCliente = React.lazy(() => import('../pages/cobranca/cobranca_lote_cliente'));
const ContatosTransportadoras = React.lazy(() => import('../pages/avaria/contatos_transp'));
const SpecialCondition = React.lazy(() => import('../pages/avaria/special_condition'));
const Usuarios = React.lazy(() => import('../pages/usuarios/usuarios'));
const PapeisPermissoes = React.lazy(() => import('../pages/permissoes/papeis_permissoes'));
const MaintenancePage = React.lazy(() => import('../pages/manutencao/main'));
const StatusSistema = React.lazy(() => import('../pages/manutencao/status_sistema'));
const PainelBloqueio = React.lazy(() => import('../pages/avaria/painel_bloqueio'));
const DashboardAtendimento = React.lazy(() => import('../pages/dashboards/dashboard_atendimento'));
const DashboardsGerenciais = React.lazy(() => import('../pages/dashboards/gerenciais'));
const DashboardsConfig = React.lazy(() => import('../pages/dashboards/config'));
const MaerskOrdens = React.lazy(() => import('../pages/relatorios/maersk_ordens'));
const Desenvolvimento = React.lazy(() => import('../pages/manutencao/desenvolvimento'));
const TabelaAvariasArmadores = React.lazy(() => import('../pages/manutencao/tabela_avarias_armadores'));
const PainelAgendamento = React.lazy(() => import('../pages/agendamento/painel_agendamento'));
const Booking = React.lazy(() => import('../pages/agendamento/booking'));
const DashboardAgendamento = React.lazy(() => import('../pages/agendamento/dashboard_agendamento'));
const DashboardProdutividade = React.lazy(() => import('../pages/agendamento/dashboard_produtividade'));
const DashboardDevolucoes = React.lazy(() => import('../pages/gate/dashboard_devolucoes'));
const DashboardAgendamentosGate = React.lazy(() => import('../pages/agendamento/dashboard_agendamentos_gate'));
const DashboardAvaria = React.lazy(() => import('../pages/avaria/dashboard_avaria'));
const RelatorioVistoriaStatus = React.lazy(() => import('../pages/avaria/relatorio_vistoria_status'));
const ControleCelulares = React.lazy(() => import('../pages/t.i/controle_celulares'));
const ControleComputadores = React.lazy(() => import('../pages/t.i/controle_computadores'));
const TesteImpressora = React.lazy(() => import('../pages/t.i/teste_impressora'));
const Perfil = React.lazy(() => import('../pages/manutencao/perfil'));
const Destinos = React.lazy(() => import('../pages/manutencao/destinos'));
const DirecionamentoAreas = React.lazy(() => import('../pages/manutencao/direcionamento'));
const EntConsulta = React.lazy(() => import('../pages/entidades/cad_entidade_consulta'));
const BloqueioMotoristas = React.lazy(() => import('../pages/manutencao/bloqueio_motoristas'));
const BloqueioTransportadoras = React.lazy(() => import('../pages/manutencao/bloqueio_transportadoras'));
const BookingEdi = React.lazy(() => import('../pages/manutencao/booking-edi'));
const MaerskEdiConfig = React.lazy(() => import('../pages/manutencao/maersk_edi_config'));
const ConsultaVistoria = React.lazy(() => import('../pages/public/consulta_vistoria'));
const SeaboxLogin = React.lazy(() => import('../pages/seabox/seabox_login'));
const SeaboxPainel = React.lazy(() => import('../pages/seabox/seabox_painel'));
const CodigosEquivArmadores = React.lazy(() => import('../pages/manutencao/codigos_equiv_armadores'));
const Auditoria = React.lazy(() => import('../pages/auditoria/auditoria'));
const SyncPendencias = React.lazy(() => import('../pages/sync_pendencias/sync_pendencias'));
const ValidarChancela = React.lazy(() => import('../pages/manutencao/validar_chancela'));
// TEMPORARIO: rota de teste do simulador de vistoria
const SimuladorVistoria = React.lazy(() => import('../pages/manutencao/simulador_vistoria'));
const BloqueioImportadores = React.lazy(() => import('../pages/avaria/bloqueio_importadores'));
const ConsultaDevolucoes = React.lazy(() => import('../pages/logistica/consulta_devolucoes'));
const EmbarquesRealizados = React.lazy(() => import('../pages/logistica/embarques'));
const PreCadastroDevolucao = React.lazy(() => import('../pages/logistica/pre_cadastro_devolucao'));
const CadDevolucaoRcSuzano = React.lazy(() => import('../pages/logistica/cad_devolucao_rc_suzano'));
const LancamentoCobrancas = React.lazy(() => import('../pages/avaria/lancamento_cobrancas'));
const PreEntrada = React.lazy(() => import('../pages/gate/pre_entrada'));
const TestDocumentos = React.lazy(() => import('../pages/documentos/test_documentos'));
const PainelCancExpo = React.lazy(() => import('../pages/operacional/painel_canc_expo'));
const SaldosPatios = React.lazy(() => import('../pages/operacional/saldos_patios'));
const ConfigPatio = React.lazy(() => import('../pages/operacional/config_patio'));
const PainelDevolucao = React.lazy(() => import('../pages/operacional/painel_devolucao'));
const PesquisaFotos = React.lazy(() => import('../pages/avaria/pesquisa_fotos'));
const PesquisaFotosAvaria = React.lazy(() => import('../pages/avaria/pesquisa_fotos_avaria'));
const FatGestaoArmadores = React.lazy(() => import('../pages/faturamento/gestao_armadores'));
const FatGeracao = React.lazy(() => import('../pages/faturamento/geracao'));
const FatConsulta = React.lazy(() => import('../pages/faturamento/consulta_faturas'));
const FatConferencia = React.lazy(() => import('../pages/faturamento/conferencia'));
const FatFaturas = React.lazy(() => import('../pages/faturamento/faturas'));

/**
 * Guarda de rota. Duas formas de autorizar, nesta ordem:
 *
 *  1. `menuKey` (PREFERIDO) — usa a permissão por item de menu, editável na
 *     tela /manutencao/papeis. Mesma key de src/config/menu.js.
 *  2. `requiredPermissions` (LEGADO) — lista de papéis fixa no código.
 *
 * Rota que passa as duas: `menuKey` manda. Migrar uma rota = trocar
 * requiredPermissions por menuKey. Ver RBAC.md.
 */
const Private = ({ Item, requiredPermissions, menuKey, isLazy = false }) => {
  const { permissions, loaded, can } = useAuth();
  const token = localStorage.getItem('token');

  if (!loaded) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Carregando...</span>
          </div>
          <div className="mt-3">
            <p className="text-muted">Carregando autenticação...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!token) {
    return <Navigate to="/login" />;
  }

  const hasPermission = menuKey
    ? can(menuKey)
    : (!requiredPermissions || requiredPermissions.length === 0
      || requiredPermissions.some(permission => permissions[permission]));

  if (!hasPermission) {
    return <Navigate to="/unauthorized" />;
  }

  if (isLazy) {
    return (
      <Suspense fallback={
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Carregando...</span>
            </div>
            <div className="mt-3">
              <p className="text-muted">Carregando componente...</p>
            </div>
          </div>
        </div>
      }>
        <Item />
      </Suspense>
    );
  }

  return <Item />;
};

const RouteApp = () => {
  return (
    <AuthProvider>
      <BrowserRouter>
      <SessionTimeout>
        <Fragment>
          <Routes>
            {/* Rotas Públicas SEM sidebar (sem autenticação necessária) */}
            <Route path="/login" element={<Login />} />
            <Route path="/cobranca_cliente" element={
              <Suspense fallback={
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
                  <div className="text-center">
                    <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                      <span className="visually-hidden">Carregando...</span>
                    </div>
                    <div className="mt-3">
                      <p className="text-muted">Carregando componente...</p>
                    </div>
                  </div>
                </div>
              }>
                <CobrancaCliente />
              </Suspense>
            } />
            {/* Portal do lote de cobrança — o link de qualquer container do lote redireciona pra cá */}
            <Route path="/cobranca_lote_cliente" element={
              <Suspense fallback={
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
                  <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                </div>
              }>
                <CobrancaLoteCliente />
              </Suspense>
            } />

            <Route path="/consulta" element={
              <Suspense fallback={
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
                  <div className="text-center">
                    <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                      <span className="visually-hidden">Carregando...</span>
                    </div>
                    <div className="mt-3">
                      <p className="text-muted">Carregando componente...</p>
                    </div>
                  </div>
                </div>
              }>
                <ConsultaVistoria />
              </Suspense>
            } />

            <Route path="/documentos_teste" element={<Private Item={TestDocumentos}
            menuKey="config.documentos_teste" isLazy={true} />}/>

            <Route path="/seabox_login" element={
              <Suspense fallback={
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
                  <div className="spinner-border text-primary" role="status" />
                </div>
              }>
                <SeaboxLogin />
              </Suspense>
            } />

            <Route path="/seabox" element={
              <Suspense fallback={
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
                  <div className="spinner-border text-primary" role="status" />
                </div>
              }>
                <SeaboxPainel />
              </Suspense>
            } />

            {/* Rotas com sidebar persistente (Navbar fica montada uma única vez, só o conteúdo troca) */}
            <Route element={<AppLayout />}>
              <Route path="/atend" element={
                <Suspense fallback={
                  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
                    <div className="text-center">
                      <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                      <div className="mt-3">
                        <p className="text-muted">Carregando componente...</p>
                      </div>
                    </div>
                  </div>
                }>
                  <DashboardAtendimento />
                </Suspense>
              } />
              <Route path="/unauthorized" element={<Unauthorized />} />

              <Route path="*" element={<Private Item={Home}
              menuKey="home" isLazy={true} />} />

              <Route path="/home" element={<Private Item={Home}
              menuKey="home" isLazy={true} />} />

              <Route path="/estimativa" element={<Private Item={Estimativa}
              menuKey="logistica.painel" isLazy={true} />}/>

              <Route path="/carregamento" element={<Private Item={Carregamento}
              menuKey="gate.carregamento" isLazy={true} />}/>

              <Route path="/janelas_devolucao" element={<Private Item={JanelasDevolucao}
              menuKey="logistica.devolucoes.janelas" isLazy={true} />}/>

              <Route path="/no_show" element={<Private Item={NoShow}
              menuKey="documental.no_show" isLazy={true} />}/>

              <Route path="/parceiros" element={<Private Item={Parceiros}
              menuKey="documental.parceiros" isLazy={true} />}/>

              <Route path="/open_turn_in" element={<Private Item={OpenTurnIn}
              menuKey="gate.open_turn_in" isLazy={true} />}/>

              <Route path="/pre_entrada_lote" element={<Private Item={PreEntradaLote}
              menuKey="gate.pre_entrada_lote" isLazy={true} />}/>

              <Route path="/gerenciamento_lacres" element={<Private Item={GerenciamentoLacres}
              menuKey="gerenciar.lacres" isLazy={true} />}/>

              <Route path="/gate_in" element={<Private Item={GateIn}
              menuKey="gate.gate_in" isLazy={true} />}/>

              <Route path="/gate_out" element={<Private Item={GateOut}
              menuKey="gate.gate_out" isLazy={true} />}/>

              <Route path="/pagamento_lote" element={<Private Item={PagamentoLote}
              menuKey="avaria.pagamento_lote" isLazy={true} />}/>

              <Route path="/sobre_rodas" element={<Private Item={SobreRodas}
              menuKey="avaria.sobre_rodas" isLazy={true} />}/>

              <Route path="/controle_cobrancas" element={<Private Item={ControleCobrancas}
              menuKey="avaria.controle_cobrancas" isLazy={true} />}/>

              <Route path="/cobranca_lote" element={<Private Item={CobrancaLote}
              menuKey="avaria.cobranca_lote" isLazy={true} />}/>

              <Route path="/painel_vistorias" element={<Private Item={PainelVistorias}
              menuKey="logistica.painel_vistorias" isLazy={true} />}/>

              <Route path="/protocolo_estimativas" element={<Private Item={ProtocoloEstimativa}
              menuKey="documental.protocolo" isLazy={true} />}/>

              <Route path="/atualizacao_status" element={<Private Item={AtualizacaoStatus}
              menuKey="documental.atualizacao_status" isLazy={true} />}/>

              <Route path="/isencoes_a_definir" element={<Private Item={IsencoesADefinir}
              menuKey="documental.isencoes_a_definir" isLazy={true} />}/>

              <Route path="/painel_bloqueio" element={<Private Item={PainelBloqueio}
              menuKey="avaria.painel_bloqueio" isLazy={true} />}/>

              <Route path="/contatos_transp" element={<Private Item={ContatosTransportadoras}
              menuKey="avaria.contatos_transp" isLazy={true} />}/>

              <Route path="/special_condition" element={<Private Item={SpecialCondition}
              menuKey="avaria.special_condition" isLazy={true} />}/>

              <Route path="/relatorios" element={<Private Item={ReportList}
              menuKey="relatorios.lista" isLazy={true} />}/>

              <Route path="/inventario" element={<Private Item={Inventario}
              menuKey="relatorios.inventario" isLazy={true} />}/>

              {/* Movimentação IN/OUT: mesma tela, o sentido vem do path (o Private
                  monta <Item /> sem props, então o param é lido com useParams). */}
              <Route path="/relatorios/movimentacao/:sentido" element={<Private Item={Movimentacao}
              menuKey="relatorios.movimentacao" isLazy={true} />}/>

              <Route path="/produtividade_vistorias" element={<Private Item={ProdutividadeVistorias}
              menuKey="relatorios.produtividade_vistorias" isLazy={true} />}/>

              <Route path="/saldos_patios" element={<Private Item={SaldosPatios}
              menuKey="gerenciar.saldos_patios" isLazy={true} />}/>

              {/* Configuração de Pátio — armadores e prioridade (por armador, não por
                  pátio), janela do portão, expediente e dias fechados. Os mesmos perfis
                  que o backend aceita em TIPOS_USUARIO_CONFIG: admin, gGeral, supDoc, dev.
                  Quem não estiver neles abre em somente leitura. */}
              <Route path="/config_patio" element={<Private Item={ConfigPatio}
              menuKey="gerenciar.config_patio" isLazy={true} />}/>

              <Route path="/manutencao" element={<Private Item={MaintenancePage}
              menuKey="config.manutencao" isLazy={true} />}/>

              <Route path="/status-sistema" element={<Private Item={StatusSistema}
              menuKey="config.status_sistema" isLazy={true} />}/>

              <Route path="/painel_agendamentos" element={<Private Item={PainelAgendamento}
              menuKey="agendamento.painel" isLazy={true} />}/>

              <Route path="/booking" element={<Private Item={Booking}
              menuKey="agendamento.bookings" isLazy={true} />}/>

              <Route path="/relatorio_agendamentos" element={<Private Item={DashboardAgendamento}
              menuKey="relatorios.agendamentos" isLazy={true} />}/>

              <Route path="/produtividade_agendamentos" element={<Private Item={DashboardProdutividade}
              menuKey="relatorios.produtividade" isLazy={true} />}/>

              <Route path="/dashboard_devolucoes" element={<Private Item={DashboardDevolucoes}
              menuKey="relatorios.dashboard_devolucoes" isLazy={true} />}/>

              <Route path="/saidas_agendamentos" element={<Private Item={DashboardAgendamentosGate}
              menuKey="relatorios.saidas_agendamentos" isLazy={true} />}/>

              <Route path="/dashboard_avaria" element={<Private Item={DashboardAvaria}
              menuKey="relatorios.dashboard_avaria" isLazy={true} />}/>

              <Route path="/desenvolvimento" element={<Private Item={Desenvolvimento}
              menuKey="config.desenvolvimento" isLazy={true} />}/>

              <Route path="/manutencao/tabelas-armadores" element={<Private Item={TabelaAvariasArmadores}
              menuKey="gerenciar.tabelas_avarias" isLazy={true} />}/>

              <Route path="/usuarios" element={<Private Item={Usuarios}
              menuKey="gerenciar.usuarios" isLazy={true} />}/>

              {/* Administração do RBAC: papéis x telas x ações (ddl_rbac.sql) */}
              <Route path="/manutencao/papeis" element={<Private Item={PapeisPermissoes}
              menuKey="config.papeis" isLazy={true} />}/>

              <Route path="/info" element={<Private Item={CompanyInfo}
              menuKey="config.info" isLazy={true} />}/>

              <Route path="/controle_celulares" element={<Private Item={ControleCelulares}
              menuKey="ti.celulares" isLazy={true} />}/>
              <Route path="/controle_computadores" element={<Private Item={ControleComputadores}
              menuKey="ti.computadores" isLazy={true} />}/>
              <Route path="/teste_impressora" element={<Private Item={TesteImpressora}
              menuKey="ti.impressora" isLazy={true} />}/>

              <Route path="/perfil" element={<Private Item={Perfil}
              menuKey="config.perfil" isLazy={true} />}/>

              <Route path="/notas_atualizacao" element={<Private Item={NotasAtualizacaoPage}
              menuKey="config.notas_atualizacao" isLazy={true} />}/>

              <Route path="/manutencao/tabela-destinos" element={<Private Item={Destinos}
              menuKey="gerenciar.tabela_destinos" isLazy={true} />}/>

              <Route path="/manutencao/direcionamento-areas" element={<Private Item={DirecionamentoAreas}
              menuKey="gerenciar.direcionamento" isLazy={true} />}/>

              <Route path="/manutencao/entidades" element={<Private Item={EntConsulta}
              menuKey="cadastros" isLazy={true} />}/>

              <Route path="/manutencao/bloqueio-motoristas" element={<Private Item={BloqueioMotoristas}
              menuKey="gerenciar.bloqueios.motoristas" isLazy={true} />}/>

              <Route path="/manutencao/bloqueio-transportadoras" element={<Private Item={BloqueioTransportadoras}
              menuKey="gerenciar.bloqueios.transportadoras" isLazy={true} />}/>

              <Route path="/manutencao/booking-edi" element={<Private Item={BookingEdi}
              menuKey="agendamento.booking_edi" isLazy={true} />}/>

              <Route path="/manutencao/maersk-edi" element={<Private Item={MaerskEdiConfig}
              menuKey="config.maersk_edi" isLazy={true} />}/>

              <Route path="/manutencao/validar-chancela" element={<Private Item={ValidarChancela}
              menuKey="gerenciar.validar_chancela" isLazy={true} />}/>

              <Route path="/manutencao/codigos-equivalencia" element={<Private Item={CodigosEquivArmadores}
              menuKey="gerenciar.codigos_equivalencia" isLazy={true} />}/>

              <Route path="/auditoria" element={<Private Item={Auditoria}
              menuKey="gerenciar.auditoria" isLazy={true} />}/>
              <Route path="/sync_pendencias" element={<Private Item={SyncPendencias}
              menuKey="gerenciar.sync_pendencias" isLazy={true} />}/>

              {/* TEMPORARIO: rota de teste do simulador de vistoria */}
              <Route path="/manutencao/simulador-vistoria" element={<Private Item={SimuladorVistoria}
              menuKey="config.simulador_vistoria" isLazy={true} />}/>

              <Route path="/avaria/bloqueio-importadores" element={<Private Item={BloqueioImportadores}
              menuKey="gerenciar.bloqueios.importadores" isLazy={true} />}/>

              <Route path="/consulta_devolucoes" element={<Private Item={ConsultaDevolucoes}
              menuKey="logistica.devolucoes.consulta" isLazy={true} />}/>

              {/* Embarques feitos no aplicativo (operacional.embarques, leitura) */}
              <Route path="/embarques" element={<Private Item={EmbarquesRealizados}
              menuKey="logistica.embarques" isLazy={true} />}/>

              <Route path="/pre_cadastro_devolucao" element={<Private Item={PreCadastroDevolucao}
              menuKey="logistica.devolucoes.pre_cadastro" isLazy={true} />}/>

              <Route path="/cadastro_devolucao_rc_suzano" element={<Private Item={CadDevolucaoRcSuzano}
              menuKey="logistica.devolucoes.rc_suzano" isLazy={true} />}/>

              <Route path="/lancamento_cobrancas" element={<Private Item={LancamentoCobrancas}
              menuKey="avaria.lancamento_cobrancas" isLazy={true} />}/>

              <Route path="/pre_entrada" element={<Private Item={PreEntrada}
              menuKey="gate.pre_entrada" isLazy={true} />}/>

              <Route path="/cancelamento-expo" element={<Private Item={PainelCancExpo}
              menuKey="documental.cancelamento_expo" isLazy={true} />}/>

              <Route path="/painel_devolucao" element={<Private Item={PainelDevolucao}
              menuKey="logistica.devolucoes.painel" isLazy={true} />}/>

              <Route path="/relatorio_vistoria_status" element={<Private Item={RelatorioVistoriaStatus}
              menuKey="relatorios.vistoria_status" isLazy={true} />}/>

              <Route path="/pesquisa_fotos" element={<Private Item={PesquisaFotos}
              menuKey="documental.pesquisa_fotos" isLazy={true} />}/>

              <Route path="/pesquisa_fotos_avaria" element={<Private Item={PesquisaFotosAvaria}
              menuKey="documental.pesquisa_fotos_avaria" isLazy={true} />}/>
            </Route>
            <Route path="/dashboards/gerenciais" element={<Private Item={DashboardsGerenciais}
            menuKey="relatorios.dashboards_gerenciais" isLazy={true} />}/>

            <Route path="/relatorios/maersk-ordens" element={<Private Item={MaerskOrdens}
            menuKey="relatorios.maersk_ordens" isLazy={true} />}/>

            <Route path="/configuracoes/dashboards" element={<Private Item={DashboardsConfig}
            menuKey="config.dashboards" isLazy={true} />}/>

            <Route path="/faturamento/armadores" element={<Private Item={FatGestaoArmadores}
            menuKey="faturamento.armadores" isLazy={true} />}/>

            <Route path="/faturamento/geracao" element={<Private Item={FatGeracao}
            menuKey="faturamento.geracao" isLazy={true} />}/>

            <Route path="/faturamento/consulta" element={<Private Item={FatConsulta}
            menuKey="faturamento.consulta" isLazy={true} />}/>

            <Route path="/faturamento/conferencia" element={<Private Item={FatConferencia}
            menuKey="faturamento.conferencia" isLazy={true} />}/>

            <Route path="/faturamento/faturas" element={<Private Item={FatFaturas}
            menuKey="faturamento.faturas" isLazy={true} />}/>

          </Routes>
        </Fragment>
      </SessionTimeout>
      </BrowserRouter>
    </AuthProvider>
  );
};

export default RouteApp;
